import type { ReactNode } from "react";
import SvgIcon from "@mui/material/SvgIcon";
import {
  getCampaignIcon,
  getContactIcon,
  getGroupIcon,
  getHomeIcon,
  getIntegrationIcon,
  getReportIcon,
  getTemplateIcon,
} from "utils/DrawerIcons";
import strings from "global/constants/StringConstants";
import { store } from "utils/store";
import viewpaths from "global/constants/ViewPathConstants";

export interface Item {
  disabled?: boolean;
  external?: boolean;
  icon?: ReactNode;
  activeIcon?: ReactNode;
  items?: Item[];
  label?: ReactNode;
  path?: string;
  title: string;
  accessTo: string[];
  accessToResource: string[];
  underDevelopment?: boolean;
}

export interface Section {
  items: Item[];
  subheader?: string;
}

export const useSections = () => {
  return [
    {
      items: [
        {
          title: strings.DASHBOARD,
          path: viewpaths.dashboardViewPath,
          icon: <SvgIcon>{getHomeIcon("var(--nav-item-icon-color)")}</SvgIcon>,
          activeIcon: (
            <SvgIcon>{getHomeIcon("var(--nav-item-active-color)")}</SvgIcon>
          ),
          accessToResource: [strings.campaign, strings.contact],
          accessTo: ["all"],
        },
        {
          title: strings.CONTACTS,
          path: viewpaths.contactsViewPath,
          icon: (
            <SvgIcon>{getContactIcon("var(--nav-item-icon-color)")}</SvgIcon>
          ),
          activeIcon: (
            <SvgIcon>{getContactIcon("var(--nav-item-active-color)")}</SvgIcon>
          ),
          accessToResource: [strings.contact],
          accessTo: ["all"],
          items: [
            {
              title: strings.GROUPS,
              path: viewpaths.groupsViewPath,
              icon: (
                <SvgIcon>{getGroupIcon("var(--nav-item-icon-color)")}</SvgIcon>
              ),
              activeIcon: (
                <SvgIcon>
                  {getGroupIcon("var(--nav-item-active-color)")}
                </SvgIcon>
              ),
              accessToResource: [strings.contact],
              accessTo: ["all"],
            },
            {
              title: strings.TAGS,
              path: viewpaths.tagsViewPath,
              icon: (
                <SvgIcon>{getGroupIcon("var(--nav-item-icon-color)")}</SvgIcon>
              ),
              activeIcon: (
                <SvgIcon>
                  {getGroupIcon("var(--nav-item-active-color)")}
                </SvgIcon>
              ),
              accessToResource: [strings.contact],
              accessTo: ["all"],
            },
          ],
        },
        {
          title: strings.CAMPAIGNS,
          path: viewpaths.campaignsViewPath,
          icon: (
            <SvgIcon>{getCampaignIcon("var(--nav-item-icon-color)")}</SvgIcon>
          ),
          activeIcon: (
            <SvgIcon>{getCampaignIcon("var(--nav-item-active-color)")}</SvgIcon>
          ),
          accessToResource: [strings.campaign],
          accessTo: ["all"],
        },
        {
          title: strings.TEMPLATES,
          path: viewpaths.templatesViewPath,
          icon: (
            <SvgIcon>{getTemplateIcon("var(--nav-item-icon-color)")}</SvgIcon>
          ),
          activeIcon: (
            <SvgIcon>{getTemplateIcon("var(--nav-item-active-color)")}</SvgIcon>
          ),
          accessToResource: [],
          accessTo: ["all"],
        },
        {
          title: strings.INTEGRATIONS,
          path: viewpaths.integrationsViewPath,
          icon: (
            <SvgIcon>
              {getIntegrationIcon("var(--nav-item-icon-color)")}
            </SvgIcon>
          ),
          activeIcon: (
            <SvgIcon>
              {getIntegrationIcon("var(--nav-item-active-color)")}
            </SvgIcon>
          ),
          accessToResource: [strings.social],
          accessTo: ["all"],
        },
        {
          title: strings.REPORTS,
          path: viewpaths.reportsViewPath,
          icon: (
            <SvgIcon>{getReportIcon("var(--nav-item-icon-color)")}</SvgIcon>
          ),
          activeIcon: (
            <SvgIcon>{getReportIcon("var(--nav-item-active-color)")}</SvgIcon>
          ),
          accessToResource: [strings.contact],
          accessTo: ["all"],
          items: [
            {
              title: strings.RECIPIENT_DOMAINS,
              path: viewpaths.recipientDomainsViewPath,
              icon: (
                <SvgIcon>{getGroupIcon("var(--nav-item-icon-color)")}</SvgIcon>
              ),
              activeIcon: (
                <SvgIcon>
                  {getGroupIcon("var(--nav-item-active-color)")}
                </SvgIcon>
              ),
              accessToResource: [strings.contact],
              accessTo: ["all"],
            },
            {
              title: strings.FAILED_STATS,
              path: viewpaths.failedStatsViewPath,
              icon: (
                <SvgIcon>{getGroupIcon("var(--nav-item-icon-color)")}</SvgIcon>
              ),
              activeIcon: (
                <SvgIcon>
                  {getGroupIcon("var(--nav-item-active-color)")}
                </SvgIcon>
              ),
              accessToResource: [strings.contact],
              accessTo: ["all"],
            },
          ],
        },
        {
          title: strings.SETTINGS,
          path: viewpaths.settingsViewPath,
          accessToResource: [],
          visibleInSideNav: false,
          accessTo: ["all"],
        },
      ],
    },
  ];
};

export const hasAccessToModule = (moduleAllowedResources: string[] = []) => {
  const userResources = store.getState().auth.resources;
  const hasAccess = moduleAllowedResources.every(
    (individualResource: any) =>
      userResources.hasOwnProperty(individualResource) &&
      userResources[individualResource]?.includes(strings.fetchPermission)
  );
  return hasAccess;
};

export const useHasAccess = (path: string) => {
  return true;
  // const sections = useSections();
  // const section = sections[0].items.find((sec) => path.includes(sec.path));
  // if (section) {
  //   return permissionCheck(section);
  // }
  // return false;
};

export const hasAccessTo = (resource: string, permission: string) => {
  const resources = store.getState().auth.resources;
  if (resources.hasOwnProperty(strings.app)) {
    return true;
  }
  if (
    resources.hasOwnProperty(resource) &&
    resources[resource].includes(strings.adminPermission)
  ) {
    return true;
  }
  if (!resources.hasOwnProperty(resource)) {
    return false;
  }
  return resources[resource].includes(permission);
};

export const useMakeUserAllowedSections = () => {
  const sections = useSections();

  return sections
    .map((section) => {
      const filteredItems = section.items
        .filter((item) => item.visibleInSideNav !== false)
        .filter((item) => permissionCheck(item));

      return {
        ...section,
        items: filteredItems,
      };
    })
    .filter((section) => section.items.length > 0);
};

export const permissionCheck = (item: Item) => {
  if (item.accessTo.includes("all")) {
    // Permission will be checked in internal pages
    return true;
  }
  if (
    item.accessTo.includes("external") &&
    item.accessTo.includes("internal")
  ) {
    if (isAdmin()) {
      return true;
    }
  }
  if (
    item.accessTo.includes("external") &&
    item.accessTo.includes("internal")
  ) {
    return hasAccessToModule(item.accessToResource);
  }
  if (item.accessTo.includes("external")) {
    return isExternalUser() && hasAccessToModule(item.accessToResource);
  }
  if (isAdmin()) {
    return true;
  }
  if (item.accessTo.includes("internal")) {
    return !isExternalUser() && hasAccessToModule(item.accessToResource);
  }
  return false;
};

export const useAuthRouteRedirection = () => {
  const routes = useMakeUserAllowedSections();
  if (routes[0].items.length > 0) {
    return routes[0].items[0].path;
  }
  return viewpaths.pageNotFoundViewPath;
};

export const hasAccessToAccount = () => {
  const resources = store.getState().auth.resources;
  if (
    resources.hasOwnProperty(strings.account) &&
    resources.hasOwnProperty(strings.app)
  ) {
    return true;
  }
  return false;
};

// Access Checks

export const isExternalUser = () => {
  const resources = store.getState().auth.resources;
  return Object.keys(resources).some((resource: string) =>
    resources[resource].includes(strings.selfPermission)
  );
};

export const isAdmin = () => {
  const resources = store.getState().auth.resources;
  if (
    resources.hasOwnProperty(strings.app) &&
    resources[strings.app].includes(strings.adminPermission)
  ) {
    return true;
  }
  return false;
};

export const isDarkTheme = () => {
  const theme = store.getState().preference.theme;
  if (theme.includes("dark")) {
    return true;
  }
  return false;
};
